import { useState, useEffect, useRef, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

export function Lightbox({ images, activeIndex, isOpen, onClose, getImageUrl }) {
  const [current, setCurrent] = useState(activeIndex || 0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [touchStart, setTouchStart] = useState(null);
  const imgRef = useRef(null);

  useEffect(() => { setCurrent(activeIndex || 0); }, [activeIndex]);
  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [current]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [isOpen, current]);

  const next = useCallback(() => { setCurrent((c) => (c + 1) % images.length); }, [images.length]);
  const prev = useCallback(() => { setCurrent((c) => (c - 1 + images.length) % images.length); }, [images.length]);

  const handleZoom = (dir) => {
    setZoom((z) => {
      const nz = dir > 0 ? Math.min(z + 0.5, 4) : Math.max(z - 0.5, 1);
      if (nz === 1) setPan({ x: 0, y: 0 });
      return nz;
    });
  };

  const onMouseDown = (e) => {
    if (zoom <= 1) return;
    setDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };
  const onMouseMove = (e) => {
    if (!dragging) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const onMouseUp = () => setDragging(false);

  const onTouchStart = (e) => {
    if (e.touches.length === 1) {
      setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY, time: Date.now() });
      if (zoom > 1) {
        setDragging(true);
        setDragStart({ x: e.touches[0].clientX - pan.x, y: e.touches[0].clientY - pan.y });
      }
    }
  };
  const onTouchMove = (e) => {
    if (dragging && zoom > 1 && e.touches.length === 1) {
      setPan({ x: e.touches[0].clientX - dragStart.x, y: e.touches[0].clientY - dragStart.y });
    }
  };
  const onTouchEnd = (e) => {
    setDragging(false);
    if (!touchStart || zoom > 1) return;
    const endX = e.changedTouches[0].clientX;
    const diff = touchStart.x - endX;
    const elapsed = Date.now() - touchStart.time;
    if (elapsed < 400 && Math.abs(diff) > 50) {
      diff > 0 ? next() : prev();
    }
    setTouchStart(null);
  };

  const handleDoubleClick = () => {
    if (zoom > 1) { setZoom(1); setPan({ x: 0, y: 0 }); }
    else setZoom(2.5);
  };

  if (!isOpen || !images.length) return null;

  // Robust fallback: try original ext if webp variant 404s
  const onImgError = (e, originalPath) => {
    const img = e.currentTarget;
    if (!originalPath || !originalPath.startsWith("/api")) { img.onerror = null; return; }
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const tried = img.dataset.tried || "";
    const candidates = [
      `${BACKEND_URL}${originalPath.replace(/(_med|_thumb)\.webp$/, "_full.webp")}`,
      `${BACKEND_URL}${originalPath.replace(/(_full|_med|_thumb)\.webp$/, ".jpeg")}`,
      `${BACKEND_URL}${originalPath.replace(/(_full|_med|_thumb)\.webp$/, ".jpg")}`,
      `${BACKEND_URL}${originalPath.replace(/(_full|_med|_thumb)\.webp$/, ".png")}`,
    ];
    const next = candidates.find(u => u !== img.src && !tried.includes(u));
    if (next) { img.dataset.tried = tried + "|" + img.src; img.src = next; }
    else { img.onerror = null; }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col" data-testid="lightbox-overlay">
      {/* Controls */}
      <div className="flex items-center justify-between p-3 sm:p-4 relative z-10">
        <span className="text-white/60 text-sm">{current + 1} / {images.length}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => handleZoom(1)} className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20" data-testid="lightbox-zoom-in">
            <ZoomIn className="w-4 h-4" />
          </button>
          <button onClick={() => handleZoom(-1)} className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20" data-testid="lightbox-zoom-out">
            <ZoomOut className="w-4 h-4" />
          </button>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20" data-testid="lightbox-close">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Image */}
      <div
        className="flex-1 flex items-center justify-center overflow-hidden select-none"
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        onDoubleClick={handleDoubleClick}
        style={{ cursor: zoom > 1 ? (dragging ? "grabbing" : "grab") : "default" }}
      >
        <img
          ref={imgRef}
          src={getImageUrl(images[current])}
          alt=""
          className="max-h-full max-w-full object-contain transition-transform duration-150"
          style={{ transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)` }}
          draggable={false}
          onError={(e) => onImgError(e, images[current])}
        />
      </div>

      {/* Nav arrows */}
      {images.length > 1 && (
        <>
          <button onClick={prev} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white z-10" data-testid="lightbox-prev">
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <button onClick={next} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white z-10" data-testid="lightbox-next">
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </>
      )}

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-1.5 justify-center p-3 overflow-x-auto">
          {images.map((img, i) => (
            <button key={i} onClick={() => setCurrent(i)} className={`w-12 h-9 sm:w-16 sm:h-11 rounded-md overflow-hidden flex-shrink-0 border-2 transition-all ${current === i ? "border-[#D4AF37] opacity-100" : "border-transparent opacity-40 hover:opacity-70"}`}>
              <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" onError={(e) => onImgError(e, img)} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
